// İSG Portal — i18next kurulumu (backend dil paketi + uygulama microcopy'si).
//
// İki katman tek namespace'te ('translation') birleşir:
//   1. Uygulamanın kendi statik arayüz metinleri (tr/en/de) — `initI18n`'e verilir.
//      Bu, backend erişilemese bile **ham anahtarın sızmamasını** garantiler
//      ([[feedback_i18n_key_runtime_path]]).
//   2. Backend dil paketi (`GET /resources` — hata kodları + `label.*`), aktif
//      kültür için yüklenip birleştirilir (api-contract §6.1). Anahtar kümeleri
//      ayrık (UPPER_SNAKE vs `nokta.li`), çakışma yok.
//
// Anahtarlar `.` içerebildiğinden (`label.save`) `keySeparator:false` +
// `nsSeparator:false` — nokta literal anahtar parçasıdır, iç içe değil.
// Interpolation `{{field}}` (api-contract §6 parametreli metin).

import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { getCultureStorage } from '../platform/storage';
import { fetchLanguages, fetchResources } from './resources';
import {
  FALLBACK_CULTURE,
  type LanguageDto,
  SUPPORTED_CULTURES,
  type SupportedCulture,
} from './types';

const NAMESPACE = 'translation';
const CULTURE_STORAGE_KEY = 'isgportal.culture';

/** Uygulamanın statik arayüz metinleri: kültür → (anahtar → metin). */
export type AppResources = Record<string, Record<string, string>>;

let cachedLanguages: LanguageDto[] = [];
const loadedServerCultures = new Set<string>();

// Dil tercihi kalıcılığı platform-agnostiktir: mobil bir KV enjekte eder
// (`configureCultureStorage`); web enjekte etmezse `localStorage`'a düşer. İkisi
// de async sözleşmeyle kullanılır (initI18n/changeCulture zaten async).

async function readStoredCulture(): Promise<string | null> {
  const injected = getCultureStorage();
  if (injected) {
    try {
      return (await injected.getItem(CULTURE_STORAGE_KEY)) ?? null;
    } catch {
      return null;
    }
  }
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(CULTURE_STORAGE_KEY) : null;
  } catch {
    return null;
  }
}

async function persistCulture(culture: string): Promise<void> {
  const injected = getCultureStorage();
  if (injected) {
    try {
      await injected.setItem(CULTURE_STORAGE_KEY, culture);
    } catch {
      // Depolama kapalıysa sessiz geç — oturumluk dil yine çalışır.
    }
    return;
  }
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(CULTURE_STORAGE_KEY, culture);
    }
  } catch {
    // Depolama kapalıysa (özel mod) sessiz geç.
  }
}

// `<html lang>`'ı aktif kültürle senkron tutar. CSS `uppercase` (ve ekran okuyucu telaffuzu)
// `lang`'a göre davranır — `tr` kasası altında Almanca metin `WİCHTİG` gibi yanlış nokta'lı
// büyük İ üretir. `document` yalnız web'de var (mobile-manager de bu dosyayı kullanır ama
// RN'de global `document` yok) — bu yüzden varlığı KONTROL edilir, platform dalı açılmaz.
function syncDocumentLang(culture: string): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = culture;
}

/** Desteklenen kültüre birebir veya dil-öneki ile eşle (api-contract §6). */
function matchSupported(candidate: string | null | undefined): SupportedCulture | null {
  if (!candidate) return null;
  const lower = candidate.toLowerCase();
  const exact = SUPPORTED_CULTURES.find((c) => c.toLowerCase() === lower);
  if (exact) return exact;
  const prefix = lower.split('-')[0];
  return SUPPORTED_CULTURES.find((c) => c.toLowerCase().split('-')[0] === prefix) ?? null;
}

function pickInitialCulture(languages: LanguageDto[], storedCulture: string | null): string {
  const stored = matchSupported(storedCulture);
  if (stored) return stored;
  // `navigator` web'de dil tercihini taşır; RN'de tanımsız/eksik olabilir → atlanır.
  const browser = matchSupported(typeof navigator !== 'undefined' ? navigator.language : null);
  if (browser) return browser;
  const backendDefault = languages.find((l) => l.isDefault)?.culture;
  return matchSupported(backendDefault) ?? FALLBACK_CULTURE;
}

async function loadServerPack(culture: string): Promise<void> {
  if (loadedServerCultures.has(culture)) return;
  try {
    const pack = await fetchResources(culture);
    // Sunucunun gerçekte çözdüğü kültüre göre birleştir (istenen ≠ çözülen olabilir).
    i18next.addResourceBundle(
      pack.culture,
      NAMESPACE,
      pack.resources,
      /*deep*/ true,
      /*overwrite*/ true,
    );
    loadedServerCultures.add(pack.culture);
    loadedServerCultures.add(culture);
  } catch {
    // Backend erişilemezse uygulama microcopy'si devreye girer (ham anahtar sızmaz).
  }
}

/**
 * i18next'i başlatır: uygulama microcopy'sini yükler, backend dillerini + aktif
 * kültürün dil paketini çeker. Route'lar render edilmeden ÖNCE `await` edilir.
 */
export async function initI18n(appResources: AppResources): Promise<void> {
  // Backend dilleri (selector + varsayılan çözümü). Erişilemezse desteklenen sabit
  // kümeyi kullan (uygulama yine ayakta kalır).
  try {
    cachedLanguages = await fetchLanguages();
  } catch {
    cachedLanguages = SUPPORTED_CULTURES.map((c) => ({
      culture: c,
      name: c,
      isDefault: c === FALLBACK_CULTURE,
    }));
  }

  const initialCulture = pickInitialCulture(cachedLanguages, await readStoredCulture());

  const resources = Object.fromEntries(
    Object.entries(appResources).map(([culture, dict]) => [culture, { [NAMESPACE]: dict }]),
  );

  await i18next.use(initReactI18next).init({
    resources,
    lng: initialCulture,
    fallbackLng: FALLBACK_CULTURE,
    defaultNS: NAMESPACE,
    ns: [NAMESPACE],
    keySeparator: false,
    nsSeparator: false,
    interpolation: { escapeValue: false },
    returnNull: false,
  });

  // İlk değer + her sonraki değişiklik (yalnız `changeCulture` üzerinden değil, i18next'in
  // kendi `changeLanguage`'ı doğrudan çağrılsa bile — `languageChanged` olayı her ikisini de
  // kapsar). Dinleyici `initI18n` ömrü boyunca bir kez kurulur.
  syncDocumentLang(initialCulture);
  i18next.on('languageChanged', syncDocumentLang);

  await loadServerPack(initialCulture);
  await persistCulture(initialCulture);
}

/** Aktif dili değiştirir: gerekiyorsa backend paketini yükler + tercihi kalıcılaştırır. */
export async function changeCulture(culture: string): Promise<void> {
  const target = matchSupported(culture) ?? culture;
  await loadServerPack(target);
  await i18next.changeLanguage(target);
  await persistCulture(target);
}

/** Dil seçici için aktif diller (init sırasında yüklenen; boşsa sabit küme). */
export function getAvailableLanguages(): LanguageDto[] {
  return cachedLanguages.length > 0
    ? cachedLanguages
    : SUPPORTED_CULTURES.map((c) => ({ culture: c, name: c, isDefault: c === FALLBACK_CULTURE }));
}

/** Şu anki aktif kültür. */
export function currentCulture(): string {
  return i18next.language;
}

export { i18next };
